import { Array } from "@siteimprove/alfa-array";
import { Comparable } from "@siteimprove/alfa-comparable";
import { Iterable } from "@siteimprove/alfa-iterable";
import { Serializable } from "@siteimprove/alfa-json";
import { Map } from "@siteimprove/alfa-map";
import { None, Option } from "@siteimprove/alfa-option";
import { Predicate } from "@siteimprove/alfa-predicate";
import { Set } from "@siteimprove/alfa-set";
import { Branch, Empty, Leaf, Node } from "./node.js";
const { not } = Predicate;
const { compareComparable } = Comparable;
/**
 * @public
 */
export class List {
    static of(...values) {
        const size = values.length;
        // Fast path: The values fit within the tail.
        if (size <= Node.Capacity) {
            return new List(Empty, Leaf.of(values), 0, size);
        }
        return values.reduce((list, value) => list._push(value), List.empty());
    }
    static _empty = new List(Empty, Empty, 0, 0);
    static empty() {
        return this._empty;
    }
    _head;
    _tail;
    _shift;
    _size;
    constructor(head, tail, shift, size) {
        this._head = head;
        this._tail = tail;
        this._shift = shift;
        this._size = size;
    }
    get size() {
        return this._size;
    }
    isEmpty() {
        return this._tail.isEmpty();
    }
    forEach(callback) {
        Iterable.forEach(this, callback);
    }
    map(mapper) {
        let index = 0;
        const tail = this._tail.map((value) => mapper(value, index++));
        const head = this._head.map((value) => mapper(value, index++));
        return new List(head, tail, this._shift, this._size);
    }
    apply(mapper) {
        return mapper.flatMap((mapper) => this.map(mapper));
    }
    flatMap(mapper) {
        return this.reduce((list, value, index) => list.concat(mapper(value, index)), List.empty());
    }
    flatten() {
        return this.flatMap((list) => list);
    }
    reduce(reducer, accumulator) {
        return Iterable.reduce(this, reducer, accumulator);
    }
    reduceWhile(predicate, reducer, accumulator) {
        return Iterable.reduceWhile(this, predicate, reducer, accumulator);
    }
    reduceUntil(predicate, reducer, accumulator) {
        return Iterable.reduceUntil(this, predicate, reducer, accumulator);
    }
    filter(predicate) {
        return List.from(Iterable.filter(this, predicate));
    }
    reject(predicate) {
        return this.filter(not(predicate));
    }
    find(predicate) {
        return Iterable.find(this, predicate);
    }
    includes(value) {
        return Iterable.includes(this, value);
    }
    collect(mapper) {
        return List.from(Iterable.collect(this, mapper));
    }
    collectFirst(mapper) {
        return Iterable.collectFirst(this, mapper);
    }
    some(predicate) {
        return Iterable.some(this, predicate);
    }
    none(predicate) {
        return Iterable.none(this, predicate);
    }
    every(predicate) {
        return Iterable.every(this, predicate);
    }
    count(predicate) {
        return Iterable.count(this, predicate);
    }
    distinct() {
        let seen = Set.empty();
        let list = List.empty();
        for (const value of this) {
            if (seen.has(value)) {
                continue;
            }
            seen = seen.add(value);
            list = list.append(value);
        }
        return list;
    }
    get(index) {
        if (index < 0 || index >= this._size || this._tail.isEmpty()) {
            return None;
        }
        const offset = this._size - this._tail.values.length;
        let value;
        if (index < offset) {
            value = this._head.get(index, this._shift - Node.Bits);
        }
        else {
            value = this._tail.get(index - offset);
        }
        return value;
    }
    has(index) {
        return index >= 0 && index < this._size;
    }
    set(index, value) {
        if (index < 0 || index >= this._size || this._tail.isEmpty()) {
            return this;
        }
        const offset = this._size - this._tail.values.length;
        let head = this._head;
        let tail = this._tail;
        if (index < offset) {
            if (head.isEmpty()) {
                return this;
            }
            head = head.set(index, value, this._shift);
            if (head === this._head) {
                return this;
            }
        }
        else {
            tail = tail.set(index - offset, value);
            if (tail === this._tail) {
                return this;
            }
        }
        return new List(head, tail, this._shift, this._size);
    }
    insert(index, value) {
        if (index < 0 || index > this.size) {
            return this;
        }
        if (index === 0) {
            return this.prepend(value);
        }
        if (index === this.size) {
            return this.append(value);
        }
        return List.from(Iterable.concat(Iterable.take(this, index), Iterable.from([value]), Iterable.skip(this, index)));
    }
    append(value) {
        return this._push(value);
    }
    prepend(value) {
        return List.of(value).concat(this);
    }
    concat(iterable) {
        return Iterable.reduce(iterable, (list, value) => list._push(value), this);
    }
    subtract(iterable) {
        return List.from(Iterable.subtract(this, iterable));
    }
    intersect(iterable) {
        return List.from(Iterable.intersect(this, iterable));
    }
    tee(callback, ...args) {
        callback(this, ...args);
        return this;
    }
    zip(iterable) {
        return List.from(Iterable.zip(this, iterable));
    }
    first() {
        return this._tail.isEmpty() ? None : Option.of(this._tail.values[0]);
    }
    last() {
        return Iterable.last(this);
    }
    take(count) {
        return List.from(Iterable.take(this, count));
    }
    takeWhile(predicate) {
        return List.from(Iterable.takeWhile(this, predicate));
    }
    takeUntil(predicate) {
        return this.takeWhile(not(predicate));
    }
    takeLast(count = 1) {
        return List.from(Iterable.takeLast(this, count));
    }
    takeLastWhile(predicate) {
        return List.from(Iterable.takeLastWhile(this, predicate));
    }
    takeLastUntil(predicate) {
        return this.takeLastWhile(not(predicate));
    }
    skip(count) {
        return List.from(Iterable.skip(this, count));
    }
    skipWhile(predicate) {
        return List.from(Iterable.skipWhile(this, predicate));
    }
    skipUntil(predicate) {
        return this.skipWhile(not(predicate));
    }
    skipLast(count = 1) {
        let list = this;
        while (count-- > 0) {
            list = list._pop();
        }
        return list;
    }
    skipLastWhile(predicate) {
        return List.from(Iterable.skipLastWhile(this, predicate));
    }
    skipLastUntil(predicate) {
        return this.skipLastWhile(not(predicate));
    }
    trim(predicate) {
        return this.trimLeading(predicate).trimTrailing(predicate);
    }
    trimLeading(predicate) {
        return this.skipWhile(predicate);
    }
    trimTrailing(predicate) {
        return this.skipLastWhile(predicate);
    }
    rest() {
        return this.skip(1);
    }
    slice(start, end) {
        return List.from(Iterable.slice(this, start, end));
    }
    reverse() {
        return List.from(Iterable.reverse(this));
    }
    join(separator) {
        return Iterable.join(this, separator);
    }
    sort() {
        return this.sortWith(compareComparable);
    }
    sortWith(comparer) {
        return List.from(Iterable.sortWith(this, comparer));
    }
    compare(iterable) {
        return this.compareWith(iterable, compareComparable);
    }
    compareWith(iterable, comparer) {
        return Iterable.compareWith(this, iterable, comparer);
    }
    groupBy(grouper) {
        return this.reduce((groups, value) => {
            const group = grouper(value);
            return groups.set(group, groups
                .get(group)
                .getOrElse(() => List.empty())
                ._push(value));
        }, Map.empty());
    }
    equals(value) {
        return (value instanceof List &&
            value._size === this._size &&
            value._head.equals(this._head) &&
            value._tail.equals(this._tail));
    }
    hash(hash) {
        for (const value of this) {
            hash.writeUnknown(value);
        }
        hash.writeUint32(this._size);
    }
    *[Symbol.iterator]() {
        yield* this._head;
        yield* this._tail;
    }
    toArray() {
        return [...this];
    }
    toJSON(options) {
        return this.toArray().map((value) => Serializable.toJSON(value, options));
    }
    toString() {
        const values = this.join(", ");
        return `List [${values === "" ? "" : ` ${values} `}]`;
    }
    _push(value) {
        // If no tail exists yet, this means that the list is empty. We therefore
        // create a new tail with the pushed value. As the current list is empty,
        // it won't have a head. As such, there's no need to pass the head along.
        //
        // In:  List { head: Empty, tail: Empty }
        // Out: List { head: Empty, tail: Leaf(value) }
        //
        if (this._tail.isEmpty()) {
            return new List(Empty, Leaf.of([value]), 0, 1);
        }
        // If the tail has capacity for another value, we concatenate the pushed
        // value onto the current tail. The current list may or may not have a head,
        // so we pass the head along as-is.
        //
        // In:  List { head, tail }
        // Out: List { head, tail: Leaf(...tail, value) }
        //
        if (this._tail.hasCapacity()) {
            return new List(this._head, Leaf.of([...this._tail.values, value]), this._shift, this._size + 1);
        }
        // If the tail does not have capacity for another value, we need to add it
        // to the head to make room for a new tail. If the current list does not
        // have a head, we use the current tail as the new head and create a new
        // tail with the pushed value.
        //
        // In:  List { head: Empty, tail }
        // Out: List { head: tail, tail: Leaf(value) }
        //
        if (this._head.isEmpty()) {
            return new List(this._tail, Leaf.of([value]), this._shift + Node.Bits, this._size + 1);
        }
        const index = this._size - Node.Capacity;
        let head = this._head;
        let shift = this._shift;
        // If the head has overflown, we need to split it which in turn increases
        // the depth of the list.
        if (head.isLeaf() || index === Node.overflow(shift)) {
            head = Branch.of([head]);
            shift += Node.Bits;
        }
        else {
            head = head.clone();
        }
        let prev = head;
        let level = shift - Node.Bits;
        // We now add the tail to the head in order to make room for a new tail.
        while (level > 0) {
            const i = Node.fragment(index, level);
            level -= Node.Bits;
            const next = prev.nodes[i];
            if (next === undefined) {
                if (level === 0) {
                    prev.nodes[i] = this._tail;
                }
                else {
                    prev.nodes[i] = prev = Branch.empty();
                }
            }
            else {
                prev.nodes[i] = prev = next.clone();
            }
        }
        // With the current tail inserted into the new head, we can now make a new
        // tail with the pushed value. Do note that the spread syntax is only used
        // for illustrative purposes here; the tail is actually inserted in the
        // rightmost branch of the head.
        //
        // In:  List { head, tail }
        // Out: List { head: Branch(...head, ...tail), tail: Leaf(value) }
        //
        return new List(head, Leaf.of([value]), shift, this._size + 1);
    }
    _pop() {
        // If the list has no tail then it is empty. We therefore return the list
        // itself as the pop has no effect.
        if (this._tail.isEmpty()) {
            return this;
        }
        // If the list has a size of 1 then the result of the popping its last
        // element will be an empty list.
        //
        // In:  List { head: Empty, tail: Leaf(value) }
        // Out: List { head: Empty, tail: Empty }
        //
        if (this._size === 1) {
            return List.empty();
        }
        // If the tail has more than one element, it will have a non-zero size after
        // popping the last element. We can therefore get away with removing the
        // last element from the tail and reuse the head.
        //
        // In:  List { head, tail: Leaf(...tail, value) }
        // Out: List { head, tail }
        //
        if (this._tail.values.length > 1) {
            return new List(this._head, Leaf.of(this._tail.values.slice(0, this._tail.values.length - 1)), this._shift, this._size - 1);
        }
        if (this._head.isLeaf() || this._head.isEmpty()) {
            return new List(Empty, this._head, this._shift - Node.Bits, this._size - 1);
        }
        let head = this._head.clone();
        let tail = this._tail;
        let shift = this._shift;
        const index = this._size - Node.Capacity - 1;
        let prev = head;
        let level = shift - Node.Bits;
        // We now remove the rightmost leaf of the head as this will be used as the
        // new tail.
        while (level > 0) {
            const fragment = Node.fragment(index, level);
            level -= Node.Bits;
            const next = prev.nodes[fragment];
            // Once we reach the rightmost leaf node, remove it as it will be used as
            // the new tail.
            if (next.isLeaf()) {
                prev.nodes.pop();
                tail = next;
            }
            else {
                prev = prev.nodes[fragment] = next.clone();
            }
        }
        // If the head has underflown, we unwrap its first child and use that as the
        // new head which in turn decreases the depth of the list. If the head is a
        // leaf node, we instead set the new head to null.
        if (index === Node.underflow(shift)) {
            head = head.nodes[0];
            shift -= Node.Bits;
        }
        // With the rightmost branch of the head removed, we can now use it as the
        // new tail and discard the old. Do note that the spread syntax is only used
        // for illustrative purposes here; the tail is actually removed from the
        // rightmost branch of the head.
        //
        // In:  List { head: Branch(...head, tail), tail: Leaf(value) }
        // Out: List { head, tail }
        //
        return new List(head, tail, shift, this._size - 1);
    }
}
/**
 * @public
 */
(function (List) {
    function isList(value) {
        return value instanceof List;
    }
    List.isList = isList;
    function from(iterable) {
        if (isList(iterable)) {
            return iterable;
        }
        if (Array.isArray(iterable)) {
            return fromArray(iterable);
        }
        return fromIterable(iterable);
    }
    List.from = from;
    function fromArray(array) {
        const size = array.length;
        // Fast path: The array fits within the tail.
        if (size <= Node.Capacity) {
            return List.of(...array);
        }
        return Array.reduce(array, (list, value) => list.append(value), List.empty());
    }
    List.fromArray = fromArray;
    function fromIterable(iterable) {
        return Iterable.reduce(iterable, (list, value) => list.append(value), List.empty());
    }
    List.fromIterable = fromIterable;
})(List || (List = {}));
//# sourceMappingURL=list.js.map